
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import {
    CollaboratingGroupContext,
    CrfCondition,
    CrfForm,
    DatePicker,
    FormBreadcrumbs,
    GroupedField,
    GroupedFields,
    ICrfConditionParameters,
    InstitutionContext,
    MasterGroupContext,
    Numeric,
    PageLayout,
    PatientContext,
    RouteLoading,
    TimePicker,
    ToggleButtonGroup,
    Select
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { FunctionComponent, useContext } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { ConditionMode, ValidateOn } from '@ngt/forms-core';
import * as Dtos from '../../../api/dtos';
import { AppSettings } from '../../../AppSettings';


/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface ITranslationalBloodCollectionFormProps { }

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const preSurgeryBloodSample = ({ formState }: ICrfConditionParameters<Dtos.TranslationalBloodCollection>) => formState?.value?.preSurgeryBloodSample === Dtos.LookupYesNoType.Yes;


/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */

const TranslationalBloodCollectionForm: FunctionComponent<ITranslationalBloodCollectionFormProps> = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={!canUpdateCrf}
                debug={AppSettings.debug}
            >
                <GroupedFields>
                    <GroupedField
                        propertyPath="preSurgeryBloodSample"
                        component={ToggleButtonGroup}
                    />
                    <CrfCondition
                        mode={ConditionMode.Show}
                        condition={preSurgeryBloodSample}
                        subscription={{ value: true }}
                    >
                        <GroupedField
                            disableFuture
                            propertyPath="preSurgeryBloodSampleDate"
                            component={DatePicker}
                        />
                        <GroupedField
                            propertyPath="preSurgeryBloodSampleTime"
                            component={TimePicker}
                            disableFuture={false}
                            views={['hours', 'minutes']}
                        />

                        {/* <TimeField /> */}
                    </CrfCondition>
                </GroupedFields>

                
                <CrfCondition
                    mode={ConditionMode.Show}
                    condition={preSurgeryBloodSample}
                    subscription={{ value: true }}
                >
                    <GroupedFields>
                        <GroupedField
                            propertyPath="numberStreckTubes"
                            component={Numeric}
                            label="Streck"
                        />
                    </GroupedFields>

                    <GroupedField
                        propertyPath="streckTubeExpiry"
                        component={DatePicker}
                    />
                    <GroupedField
                        propertyPath="strekStockType"
                        component={Select}
                    />
                </CrfCondition>
            </CrfForm>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default TranslationalBloodCollectionForm;
